import { z } from "zod";

/**
 * 🔴🔴 Contracts — QUỸ MUA CHUNG SUBSCRIPTION (chia GHẾ). US-E20.F5-0211-S02 (@UC9339–@UC9342).
 *
 * ⚠️⚠️ BẪY ĐẶT TÊN — ĐỌC KỸ TRƯỚC KHI THÊM SCHEMA:
 *   · `planSchema` / `groupSubscriptionSchema` (`admin-billing.ts`) = **BILLING SaaS CỦA GOPPY**
 *     (goppy thu tiền NHÓM để nhóm dùng goppy — tách-ledger-tuyệt-đối, 0 bút toán).
 *   · File này = **THỨ KHÁC HẲN**: nhóm bạn bè GÓP TIỀN MUA CHUNG gói dịch vụ BÊN THỨ BA (Netflix
 *     Family, Spotify Family, ChatGPT Team) rồi CHIA GHẾ. CÓ ledger thật.
 *   ⇒ Mọi tên ở đây mang tiền tố `subscriptionPool*` / `poolSlot*` / `poolCharge*` — KHÔNG dùng
 *     `plan*` / `groupSubscription*` (name-collision = 0, kiểm bằng test).
 *
 * B1 NO-FLOAT ở BIÊN JSON: MỌI số tiền là `string` số nguyên minor (BigInt tuần tự hoá) — không bao
 * giờ `number`. `.strict()` (dư field ⇒ reject).
 */

const NON_NEGATIVE_INTEGER_STRING = /^\d+$/;

const positiveMoneyString = (label: string) =>
  z
    .string()
    .regex(NON_NEGATIVE_INTEGER_STRING, `${label} phải là số nguyên minor (chuỗi).`)
    .refine((v) => BigInt(v) > 0n, `${label} phải > 0.`);

const nonNegativeMoneyString = (label: string) =>
  z.string().regex(NON_NEGATIVE_INTEGER_STRING, `${label} phải là số nguyên minor (chuỗi).`);

/** Ngày YYYY-MM-DD (không giờ — ổn định TZ, khớp @db.Date). */
const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Ngày phải dạng YYYY-MM-DD.");

/** Kỳ tính phí 'YYYY-MM' — khoá idempotency gia hạn (chạy 2 lần ⇒ 1 bút toán). */
export const poolPeriodKeySchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, "periodKey phải dạng YYYY-MM.");

export const SUBSCRIPTION_POOL_STATUS_VALUES = ["active", "cancelled"] as const;
export const subscriptionPoolStatusSchema = z.enum(SUBSCRIPTION_POOL_STATUS_VALUES);

export const POOL_SLOT_STATUS_VALUES = ["vacant", "occupied"] as const;
export const poolSlotStatusSchema = z.enum(POOL_SLOT_STATUS_VALUES);

/** Ai ỨNG TIỀN trả nhà cung cấp: quỹ tự trả | NGƯỜI ĐỨNG TÊN ứng (quỹ nợ lại họ). */
export const POOL_CHARGE_PAYER_VALUES = ["fund", "owner"] as const;
export const poolChargePayerSchema = z.enum(POOL_CHARGE_PAYER_VALUES);

/* ─────────────────────────────── INPUT ─────────────────────────────── */

/** @UC9339 — tạo gói mua chung: N ghế TRỐNG được sinh ngay (slotNo 1..N). */
export const createSubscriptionPoolSchema = z
  .object({
    serviceName: z.string().trim().min(1).max(100),
    /** Phí NCC MỖI KỲ (> 0). */
    totalFeeMinor: positiveMoneyString("totalFeeMinor"),
    /** Số ghế gói cho phép (≥ 1). */
    slotCount: z.number().int().min(1).max(50),
    /** NGƯỜI ĐỨNG TÊN gói (thẻ họ bị trừ) — quỹ hoàn lại khi họ ứng tiền. */
    ownerMemberId: z.string().uuid(),
  })
  .strict();
export type CreateSubscriptionPoolRequest = z.infer<typeof createSubscriptionPoolSchema>;

/** @UC9341 — cấp ghế TRỐNG cho một người (bắt đầu tính phí theo NGÀY từ `occupiedFrom`). */
export const assignPoolSlotSchema = z
  .object({
    slotNo: z.number().int().min(1),
    groupMemberId: z.string().uuid(),
    occupiedFrom: dateOnlySchema,
  })
  .strict();
export type AssignPoolSlotRequest = z.infer<typeof assignPoolSlotSchema>;

/** @UC9341 — THU HỒI ghế (người rời): ghế về `vacant` từ `vacatedTo` (INCLUSIVE — tính cả ngày đó). */
export const reclaimPoolSlotSchema = z
  .object({
    slotNo: z.number().int().min(1),
    vacatedTo: dateOnlySchema,
  })
  .strict();
export type ReclaimPoolSlotRequest = z.infer<typeof reclaimPoolSlotSchema>;

/**
 * @UC9340 @UC9342 — TÍNH PHÍ MỘT KỲ: trả NCC (EXPENSE) + sinh due từng ghế (REUSE Collection).
 * `payer='owner'` ⇒ người đứng tên ứng tiền, quỹ ghi NỢ họ (MEMBER_PAYABLE) — đối trừ sau.
 * KHÔNG nhận `idempotencyKey` từ client: khoá DETERMINISTIC = (poolId, periodKey) ⇒ chạy 2 lần ⇒
 * 1 bút toán (client không thể vô tình đẻ khoá mới rồi ghi đúp).
 */
export const chargePoolPeriodSchema = z
  .object({
    periodKey: poolPeriodKeySchema,
    /** Kỳ NỬA MỞ [periodStart, periodEnd) — periodEnd = ngày ĐẦU kỳ SAU. */
    periodStart: dateOnlySchema,
    periodEnd: dateOnlySchema,
    payer: poolChargePayerSchema.optional(),
    /** Mục đích ràng buộc (earmark E20.F3) nếu quỹ có khoá mục đích — sai/thiếu ⇒ 422, 0 bút toán. */
    purposeTag: z.string().trim().min(1).max(64).optional(),
  })
  .strict();
export type ChargePoolPeriodRequest = z.infer<typeof chargePoolPeriodSchema>;

/* ─────────────────────────────── OUTPUT ─────────────────────────────── */

export const poolSlotResponseSchema = z
  .object({
    id: z.string().uuid(),
    poolId: z.string().uuid(),
    slotNo: z.number().int().min(1),
    status: poolSlotStatusSchema,
    /** Người ĐANG ngồi (occupancy còn mở) — null = ghế trống. */
    occupantMemberId: z.string().uuid().nullable(),
    occupiedFrom: dateOnlySchema.nullable(),
  })
  .strict();
export type PoolSlotResponse = z.infer<typeof poolSlotResponseSchema>;

export const subscriptionPoolResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    serviceName: z.string(),
    totalFeeMinor: positiveMoneyString("totalFeeMinor"),
    slotCount: z.number().int().min(1),
    /** Giá NIÊM YẾT/ghế = ⌊total/slotCount⌋ — MỐC SO SÁNH, không phải số thực đóng khi có ghế trống. */
    feePerSlotMinor: nonNegativeMoneyString("feePerSlotMinor"),
    currency: z.string().length(3),
    ownerMemberId: z.string().uuid(),
    /** Lịch thu ghế (REUSE CollectionSchedule) — null = chưa bật thu định kỳ. */
    scheduleId: z.string().uuid().nullable(),
    status: subscriptionPoolStatusSchema,
    slots: z.array(poolSlotResponseSchema),
  })
  .strict();
export type SubscriptionPoolResponse = z.infer<typeof subscriptionPoolResponseSchema>;

/** Phần phí của MỘT người trong kỳ + SỐ NGÀY ngồi ghế (để giải thích "vì sao tôi trả X"). */
export const poolFeeShareResponseSchema = z
  .object({
    groupMemberId: z.string().uuid(),
    amountMinor: nonNegativeMoneyString("amountMinor"),
    seatDays: z.number().int().min(0),
  })
  .strict();
export type PoolFeeShareResponse = z.infer<typeof poolFeeShareResponseSchema>;

/**
 * @UC9340 @UC9342 — kết quả TÍNH PHÍ KỲ. 🔴 MINH BẠCH BẮT BUỘC: phơi `perSlotBaseMinor` (giá niêm
 * yết/ghế) vs số THỰC từng người + `vacantSlotCount` ⇒ người dùng THẤY mình đang gánh phần ghế
 * trống, không bị âm thầm. `residualHolderMemberId` = ai gánh ĐỒNG LẺ (P1.3 — đồng lẻ CÓ TÊN).
 */
export const poolChargeResponseSchema = z
  .object({
    id: z.string().uuid(),
    poolId: z.string().uuid(),
    periodKey: poolPeriodKeySchema,
    totalFeeMinor: positiveMoneyString("totalFeeMinor"),
    currency: z.string().length(3),
    payer: poolChargePayerSchema,
    /** Bút toán EXPENSE trả NCC (append-only). */
    expenseTxId: z.string().uuid(),
    /** Đợt thu ghế của kỳ (REUSE Collection/MemberDue — KHÔNG engine thu mới). */
    collectionId: z.string().uuid(),
    perSlotBaseMinor: nonNegativeMoneyString("perSlotBaseMinor"),
    vacantSlotCount: z.number().int().min(0),
    residualHolderMemberId: z.string().uuid().nullable(),
    shares: z.array(poolFeeShareResponseSchema),
  })
  .strict();
export type PoolChargeResponse = z.infer<typeof poolChargeResponseSchema>;
